import { useState, useMemo, useCallback, useEffect } from "react";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import ApprovalIcon from "@mui/icons-material/Approval";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import {
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import Spinner from "@components/Spinner";
import { ARInvoiceReviewFilters } from "@components/ar_invoice_components/ARInvoiceReviewFilters";
import { InvoicesTab } from "@components/ar_invoice_components/InvoicesTab";
import { MiscellaneousTab } from "@components/ar_invoice_components/MiscellaneousTab";
import { InvoiceDifferencesTab } from "@components/ar_invoice_components/InvoiceDifferencesTab";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import {
  fetchInvoiceReview,
  finalizeSelectedInvoiceDetails,
  voidSelectedInvoiceDetails,
} from "@service/arInvoiceApiService";

// One review row from /ar_invoice_api/review. Amounts are billing-rate based.
interface InvoiceReviewRow {
  "Plant ID": number | null;
  "Facility ID": number | null;
  Facility: string | null;
  "Location Code": string | null;
  "Invoice Code": string | null;
  "B&B ID": string | number | null;
  "Bill Type": string | null;
  Date: string | null;
  Payee: string | null;
  Memo: string | null;
  Amount: number | null;
  Gross: number | null;
  Hours: number | null;
  "Bill Rate": number | string | null;
  "Rate Type": string | null;
  "Overtime Rate": number | null;
  "Other Additions": number | null;
  "Other Credit": number | null;
  Rent: number | null;
  Holiday: number | null;
  PTO: number | null;
  "Pay Period": string | null;
  "Invoice #": string | null;
  Status: string | null;
  "Detail ID"?: number | null;
}

function getPreviousSunday(): string {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysSinceLastSunday = dayOfWeek + 7;
  const lastSunday = new Date(
    today.setDate(today.getDate() - daysSinceLastSunday)
  );
  lastSunday.setHours(0, 0, 0, 0);
  return formatDate(lastSunday);
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isSelectablePeriod(periodStart: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayOfWeek = today.getDay();

  const currentWeekSunday = new Date(today);
  currentWeekSunday.setDate(today.getDate() - dayOfWeek);

  const previousWeekSunday = new Date(currentWeekSunday);
  previousWeekSunday.setDate(currentWeekSunday.getDate() - 7);

  const selected = new Date(periodStart + "T00:00:00");
  return selected.getTime() >= previousWeekSunday.getTime();
}

function getEndDateStr(startStr: string): string {
  const start = new Date(startStr + "T00:00:00");
  start.setDate(start.getDate() + 6);
  return formatDate(start);
}

const StatusCellRenderer = (props: { value: string }) => {
  const getStatusClass = (status: string) => {
    if (status === "Review") return "tw-bg-yellow-100 tw-text-yellow-800";
    if (status === "Finalized") return "tw-bg-green-100 tw-text-green-800";
    if (status === "Missing") return "tw-bg-red-100 tw-text-red-800";
    if (status === "Zero Hours") return "tw-bg-blue-100 tw-text-blue-800";
    if (status === "Zero Amount") return "tw-bg-orange-100 tw-text-orange-800";
    return "";
  };

  return (
    <div
      className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-px-2 ${getStatusClass(
        props.value
      )}`}
    >
      {props.value}
    </div>
  );
};

const currencyFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  return `$${val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const decimalFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  return val.toFixed(2);
};

// Guard sizeColumnsToFit against firing while the grid is hidden/torn down.
const safeSizeColumnsToFit = (params: { api?: any }) => {
  const api = params.api;
  if (!api || api.isDestroyed?.()) return;
  try {
    api.sizeColumnsToFit();
  } catch {
    /* grid not ready / no longer mounted — ignore */
  }
};

// ── Per-plant grid (mirrors PlantPayrollGrid) ──────────────────────────────
interface PlantInvoiceGridProps {
  plantName: string;
  rows: InvoiceReviewRow[];
  columnDefs: ColDef[];
  defaultColDef: ColDef;
  periodStart: string;
  onRefresh: () => void;
  // Finalize: flip the given B&B IDs to "Finalized" in place (no refetch).
  onFinalized: (bbIds: (string | number | null)[]) => void;
}

function PlantInvoiceGrid({
  plantName,
  rows,
  columnDefs,
  defaultColDef,
  periodStart,
  onRefresh,
  onFinalized,
}: PlantInvoiceGridProps) {
  const [open, setOpen] = useState(true);
  const [badgeFilter, setBadgeFilter] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<InvoiceReviewRow[]>([]);
  const [detailRow, setDetailRow] = useState<InvoiceReviewRow | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidListOpen, setVoidListOpen] = useState(false);
  // Zero-amount finalize flow: zero-amount rows can only be finalized when the
  // entire selection is zero-amount (warned), never mixed with non-zero rows.
  const [zeroAmountOpen, setZeroAmountOpen] = useState(false);
  const [zeroAmountList, setZeroAmountList] = useState<InvoiceReviewRow[]>([]);
  const [zeroAmountOnly, setZeroAmountOnly] = useState(false);
  // Mixed "Review" + "Zero Hours" selection requires an extra confirmation
  // before the regular finalize confirmation.
  const [mixedStatusOpen, setMixedStatusOpen] = useState(false);
  const canSelect = isSelectablePeriod(periodStart);

  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const onSelectionChanged = (event: any) => {
    const selectedNodes = event.api.getSelectedNodes();
    setSelectedRows(selectedNodes.map((node: any) => node.data));
  };

  const handleRowDoubleClicked = (event: any) => {
    if (event?.data) setDetailRow(event.data as InvoiceReviewRow);
  };

  // Finalize requires EVERY selected row to be "Review" or "Zero Hours";
  // Cancel requires every selected row to be "Finalized". Any other status in
  // the selection blocks the respective action.
  const reviewRows = selectedRows.filter((r) => r.Status === "Review");
  const zeroHoursRows = selectedRows.filter((r) => r.Status === "Zero Hours");
  const finalizableRows = [...reviewRows, ...zeroHoursRows];
  const finalizedRows = selectedRows.filter((r) => r.Status === "Finalized");
  const hasNonFinalizableSelection = selectedRows.some(
    (r) => r.Status !== "Review" && r.Status !== "Zero Hours"
  );
  const hasNonFinalizedSelection = selectedRows.some(
    (r) => r.Status !== "Finalized"
  );
  // A detail already attached to a generated invoice (has an Invoice #) can't
  // be canceled — the invoice must be handled first.
  const hasInvoicedSelection = selectedRows.some(
    (r) => r["Invoice #"] != null && r["Invoice #"] !== ""
  );

  // Zero-amount check applies to "Review" rows only ("Zero Hours" rows are
  // expected to be $0.00); then the regular confirmation opens.
  const proceedToFinalizeConfirm = () => {
    const zeroAmountRows = reviewRows.filter(
      (r) => !r.Amount || parseFloat(String(r.Amount)) === 0
    );
    if (zeroAmountRows.length > 0) {
      setZeroAmountList(zeroAmountRows);
      // All selected are zero → allow with a warning. Mixed zero/non-zero →
      // block so the zero rows are finalized separately.
      setZeroAmountOnly(zeroAmountRows.length === reviewRows.length);
      setZeroAmountOpen(true);
      return;
    }
    setConfirmOpen(true);
  };

  const handleFinalizeSelected = () => {
    if (finalizableRows.length === 0) {
      alert(
        "No selected rows with status 'Review' or 'Zero Hours' to finalize."
      );
      return;
    }
    // Mixed "Review" + "Zero Hours" selection → ask for an extra confirmation
    // before the regular finalize flow.
    if (reviewRows.length > 0 && zeroHoursRows.length > 0) {
      setMixedStatusOpen(true);
      return;
    }
    proceedToFinalizeConfirm();
  };

  const handleConfirmFinalize = async () => {
    setFinalizing(true);
    try {
      const bb_ids = finalizableRows.map((r) => r["B&B ID"]);
      await finalizeSelectedInvoiceDetails({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        bb_ids,
      });
      setConfirmOpen(false);
      setSelectedRows([]);
      // Flip the selected rows to "Finalized" in place (no details refetch);
      // the Invoices grid is refreshed by the parent.
      onFinalized(bb_ids);
    } catch (err: any) {
      alert(
        err?.response?.data?.error || "Failed to generate invoice details."
      );
    } finally {
      setFinalizing(false);
    }
  };

  const handleConfirmVoid = async () => {
    setVoiding(true);
    try {
      const bb_ids = finalizedRows.map((r) => r["B&B ID"]);
      await voidSelectedInvoiceDetails({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        bb_ids,
      });
      setVoidConfirmOpen(false);
      setSelectedRows([]);
      onRefresh();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to void invoice details.");
    } finally {
      setVoiding(false);
    }
  };

  const reviewCount = rows.filter((r) => r.Status === "Review").length;
  const finalizedCount = rows.filter((r) => r.Status === "Finalized").length;
  const missingCount = rows.filter((r) => r.Status === "Missing").length;
  const zeroHoursCount = rows.filter((r) => r.Status === "Zero Hours").length;
  const zeroAmountCount = rows.filter((r) => r.Status === "Zero Amount").length;

  const displayedRows =
    badgeFilter == null ? rows : rows.filter((r) => r.Status === badgeFilter);

  const toggleBadgeFilter = (status: string | null) =>
    setBadgeFilter((prev) => (prev === status ? null : status));

  return (
    <div className="tw-w-full tw-mb-3">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
        <h2 className="tw-rounded tw-p-1 tw-bg-[#2642c9] tw-text-white">
          {plantName}
        </h2>
        <div className="tw-flex tw-items-center tw-gap-2">
          {(
            [
              {
                status: "Review",
                count: reviewCount,
                base: "tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-300",
                active: "tw-ring-2 tw-ring-yellow-500",
              },
              {
                status: "Finalized",
                count: finalizedCount,
                base: "tw-bg-green-100 tw-text-green-800 tw-border-green-300",
                active: "tw-ring-2 tw-ring-green-500",
              },
              {
                status: "Missing",
                count: missingCount,
                base: "tw-bg-red-100 tw-text-red-800 tw-border-red-300",
                active: "tw-ring-2 tw-ring-red-500",
              },
              {
                status: "Zero Hours",
                count: zeroHoursCount,
                base: "tw-bg-blue-100 tw-text-blue-800 tw-border-blue-300",
                active: "tw-ring-2 tw-ring-blue-500",
              },
              {
                status: "Zero Amount",
                count: zeroAmountCount,
                base: "tw-bg-orange-100 tw-text-orange-800 tw-border-orange-300",
                active: "tw-ring-2 tw-ring-orange-500",
              },
            ] as const
          ).map((badge) => (
            <button
              key={badge.status}
              type="button"
              onClick={() => toggleBadgeFilter(badge.status)}
              title={`Filter by ${badge.status}`}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-border ${
                badge.base
              } ${badgeFilter === badge.status ? badge.active : ""}`}
            >
              <span className="tw-font-semibold">{badge.count}</span>
              <span className="tw-text-xs">{badge.status}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => toggleBadgeFilter(null)}
            title="Show all rows"
            className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-border tw-bg-gray-100 tw-text-gray-700 tw-border-gray-300 ${
              badgeFilter == null ? "tw-ring-2 tw-ring-gray-500" : ""
            }`}
          >
            <span className="tw-font-semibold">{rows.length}</span>
            <span className="tw-text-xs">Total</span>
          </button>
          <Button
            sx={{ color: "#2642c9", textTransform: "none", minWidth: 0 }}
            onClick={() => setOpen(!open)}
          >
            {open ? "Hide" : "Show"}
          </Button>
        </div>
      </div>
      <Collapse in={open}>
        <div className="tw-w-full">
          <div
            className="ag-theme-balham tw-w-full"
            style={{ height: Math.min(displayedRows.length * 28 + 56, 400) }}
            onContextMenu={handleContextMenu}
          >
            <AgGridReact
              rowData={displayedRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              isRowSelectable={() => canSelect}
              onSelectionChanged={onSelectionChanged}
              onCellContextMenu={handleCellContextMenu}
              onRowDoubleClicked={handleRowDoubleClicked}
              onCellDoubleClicked={handleRowDoubleClicked}
              animateRows={true}
              pagination={false}
              onGridReady={safeSizeColumnsToFit}
              onGridSizeChanged={safeSizeColumnsToFit}
            />
            {ContextMenuComponent}
          </div>
          <div className="tw-border-l tw-border-r tw-border-b tw-border-gray-300 tw-p-1 tw-bg-white tw-flex tw-gap-2">
            <Button
              color="primary"
              sx={{ bgcolor: "#2642c9", textTransform: "none" }}
              variant="contained"
              startIcon={<ApprovalIcon />}
              onClick={handleFinalizeSelected}
              disabled={
                finalizableRows.length === 0 || hasNonFinalizableSelection
              }
            >
              Finalize Selected
            </Button>
            <Button
              color="error"
              sx={{ textTransform: "none" }}
              variant="contained"
              startIcon={<CancelIcon />}
              onClick={() => setVoidConfirmOpen(true)}
              disabled={
                finalizedRows.length === 0 ||
                hasNonFinalizedSelection ||
                hasInvoicedSelection
              }
            >
              Cancel Finalized
            </Button>
            {finalizableRows.length > 0 && hasNonFinalizableSelection && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                All selected rows must be "Review" or "Zero Hours" to finalize
              </span>
            )}
            {finalizedRows.length > 0 && hasNonFinalizedSelection && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                All selected rows must be "Finalized" to cancel
              </span>
            )}
            {finalizedRows.length > 0 &&
              !hasNonFinalizedSelection &&
              hasInvoicedSelection && (
                <span className="tw-text-red-500 tw-text-sm tw-self-center">
                  Cannot cancel a detail already on a generated invoice
                </span>
              )}
          </div>
        </div>
      </Collapse>

      {/* Row detail modal */}
      <Dialog
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {detailRow?.Payee ?? "Detail"}
          <IconButton onClick={() => setDetailRow(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailRow &&
            (() => {
              const orderedFields = columnDefs
                .map((c) => c.field)
                .filter(Boolean) as string[];
              const entries = orderedFields
                .filter((field) => field in detailRow)
                .map(
                  (field) => [field, (detailRow as any)[field]] as [string, any]
                );

              const currencyFields = [
                "Amount",
                "Gross",
                "Bill Rate",
                "Overtime Rate",
                "Other Additions",
                "Other Credit",
                "Rent",
                "Holiday",
                "PTO",
              ];
              const decimalFields = ["Hours"];

              const formatValue = (key: string, value: any) => {
                if (value == null) return "—";
                if (currencyFields.includes(key))
                  return currencyFormatter({ value });
                if (decimalFields.includes(key))
                  return decimalFormatter({ value });
                return String(value);
              };

              return (
                <Table
                  size="small"
                  sx={{ "& td": { fontSize: "0.75rem", py: 0.25 } }}
                >
                  <TableBody>
                    {entries.map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: "35%",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {key}
                        </TableCell>
                        <TableCell>
                          {key === "Status" ? (
                            <span
                              className={
                                value === "Review"
                                  ? "tw-bg-yellow-100 tw-text-yellow-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : value === "Finalized"
                                  ? "tw-bg-green-100 tw-text-green-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : value === "Missing"
                                  ? "tw-bg-red-100 tw-text-red-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : value === "Zero Hours"
                                  ? "tw-bg-blue-100 tw-text-blue-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : value === "Zero Amount"
                                  ? "tw-bg-orange-100 tw-text-orange-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : ""
                              }
                            >
                              {String(value ?? "—")}
                            </span>
                          ) : (
                            formatValue(key, value)
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
        </DialogContent>
      </Dialog>

      {/* Finalize confirmation modal */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Invoice Detail Generation</DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            You are about to generate invoice details for{" "}
            <strong>{finalizableRows.length}</strong> worker(s)
            {reviewRows.length > 0 && zeroHoursRows.length > 0
              ? ' with status "Review" and "Zero Hours"'
              : zeroHoursRows.length > 0
              ? ' with status "Zero Hours"'
              : ' with status "Review"'}
            .
          </p>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#2642c9", mb: 1 }}
            onClick={() => setListOpen(!listOpen)}
          >
            {listOpen ? "Hide" : "Show"} contractor list
          </Button>
          <Collapse in={listOpen}>
            <ul className="tw-list-disc tw-pl-5">
              {finalizableRows.map((r) => (
                <li key={String(r["B&B ID"])}>
                  {r.Payee} (B&B ID: {r["B&B ID"]}) — {r.Status}
                </li>
              ))}
            </ul>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={finalizing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            onClick={handleConfirmFinalize}
            disabled={finalizing}
            startIcon={
              finalizing ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <ApprovalIcon />
              )
            }
          >
            {finalizing ? "Generating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void confirmation modal */}
      <Dialog
        open={voidConfirmOpen}
        onClose={() => setVoidConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Cancel Invoice Details</DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            You are about to void invoice details for{" "}
            <strong>{finalizedRows.length}</strong> worker(s):
          </p>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#d32f2f", mb: 1 }}
            onClick={() => setVoidListOpen(!voidListOpen)}
          >
            {voidListOpen ? "Hide" : "Show"} contractor list
          </Button>
          <Collapse in={voidListOpen}>
            <ul className="tw-list-disc tw-pl-5">
              {finalizedRows.map((r) => (
                <li key={String(r["B&B ID"])}>
                  {r.Payee} (B&B ID: {r["B&B ID"]})
                </li>
              ))}
            </ul>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setVoidConfirmOpen(false)}
            disabled={voiding}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={handleConfirmVoid}
            disabled={voiding}
            startIcon={
              voiding ? <Spinner className="tw-w-4 tw-h-4" /> : <CancelIcon />
            }
          >
            {voiding ? "Voiding..." : "Confirm Void"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mixed Review + Zero Hours confirmation modal */}
      <Dialog
        open={mixedStatusOpen}
        onClose={() => setMixedStatusOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#ed6c02" }}>
          Warning: Mixed Statuses Selected
        </DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            Your selection contains <strong>{reviewRows.length}</strong>{" "}
            worker(s) with status "Review" and{" "}
            <strong>{zeroHoursRows.length}</strong> worker(s) with status "Zero
            Hours". Are you sure you want to finalize both statuses together?
            You will be asked to confirm again.
          </p>
          <ul className="tw-list-disc tw-pl-5">
            {zeroHoursRows.map((r) => (
              <li key={String(r["B&B ID"])}>
                {r.Payee} (B&B ID: {r["B&B ID"]}) — Zero Hours
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setMixedStatusOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#ed6c02", textTransform: "none" }}
            onClick={() => {
              setMixedStatusOpen(false);
              proceedToFinalizeConfirm();
            }}
            startIcon={<ApprovalIcon />}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zero-amount warning modal */}
      <Dialog
        open={zeroAmountOpen}
        onClose={() => setZeroAmountOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: zeroAmountOnly ? "#ed6c02" : "#d32f2f" }}>
          {zeroAmountOnly
            ? "Warning: Zero Amount Invoice Details"
            : "Cannot Finalize"}
        </DialogTitle>
        <DialogContent dividers>
          {zeroAmountOnly ? (
            <p className="tw-mb-2">
              You are about to generate invoice details for{" "}
              <strong>{zeroAmountList.length}</strong> worker(s) with an amount
              of <strong>$0.00</strong>. Are you sure you want to proceed?
            </p>
          ) : (
            <p className="tw-mb-2">
              Cannot finalize. The following{" "}
              <strong>{zeroAmountList.length}</strong> worker(s) have an amount
              of <strong>$0.00</strong> mixed with non-zero selections. Finalize
              the zero-amount rows separately.
            </p>
          )}
          <ul className="tw-list-disc tw-pl-5">
            {zeroAmountList.map((r) => (
              <li key={String(r["B&B ID"])}>
                {r.Payee} (B&B ID: {r["B&B ID"]})
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setZeroAmountOpen(false)}
            sx={{ textTransform: "none" }}
          >
            {zeroAmountOnly ? "Cancel" : "Close"}
          </Button>
          {zeroAmountOnly && (
            <Button
              variant="contained"
              sx={{ bgcolor: "#ed6c02", textTransform: "none" }}
              onClick={() => {
                setZeroAmountOpen(false);
                setConfirmOpen(true);
              }}
              startIcon={<ApprovalIcon />}
            >
              Proceed
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

export function ARInvoiceReviewPage(): JSX.Element {
  const [periodStart, setPeriodStart] = useState<string>(getPreviousSunday());
  const [rows, setRows] = useState<InvoiceReviewRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  // Bumped when a detail is finalized/canceled so the Invoices tab repopulates.
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);
  // Bumped after invoices are generated/canceled so the Miscellaneous tab
  // repopulates (linked misc become voided, active copies appear).
  const [miscRefreshKey, setMiscRefreshKey] = useState(0);

  // Filters
  const [searchVal, setSearchVal] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchReviewData = useCallback(async (startStr: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchInvoiceReview({
        start_date: startStr,
        end_date: getEndDateStr(startStr),
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(
        err?.response?.data?.error || "Failed to fetch invoice review data"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviewData(periodStart);
  }, [periodStart, fetchReviewData]);

  // Refresh the details grid and signal the Invoices tab to repopulate — used
  // after a detail is canceled.
  const refreshDetailsAndInvoices = useCallback(() => {
    fetchReviewData(periodStart);
    setInvoiceRefreshKey((k) => k + 1);
  }, [fetchReviewData, periodStart]);

  // On finalize: flip the selected rows to "Finalized" in place (no details
  // refetch) and signal the Invoices tab to repopulate.
  const markRowsFinalized = useCallback((bbIds: (string | number | null)[]) => {
    const idSet = new Set(bbIds.map((id) => String(id)));
    setRows((prev) =>
      prev.map((r) =>
        idSet.has(String(r["B&B ID"])) ? { ...r, Status: "Finalized" } : r
      )
    );
    setInvoiceRefreshKey((k) => k + 1);
  }, []);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (searchVal) {
      const search = searchVal.toLowerCase();
      result = result.filter(
        (row) =>
          String(row["B&B ID"] ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Payee ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Memo ?? "")
            .toLowerCase()
            .includes(search)
      );
    }
    if (statusFilter) {
      result = result.filter((row) => row.Status === statusFilter);
    }
    return result;
  }, [rows, searchVal, statusFilter]);

  // Group rows by plant + facility, ordered by Plant ID then Facility ID. Each
  // group renders its own grid labeled "Plant — Facility (Location Code)".
  const plantGroups = useMemo(() => {
    const groups: Record<
      string,
      {
        plantId: number;
        facilityId: number;
        label: string;
        rows: InvoiceReviewRow[];
      }
    > = {};
    filteredRows.forEach((row) => {
      const plantName = row.Memo ?? "Unknown";
      const facilityName = row.Facility;
      const invoiceCode = row["Invoice Code"];
      const facilityLabel = facilityName
        ? `${invoiceCode ? ` — ${facilityName} ${invoiceCode}` : ""}`
        : "";
      const label = `${plantName} ${facilityLabel}`;

      const key = `${row["Plant ID"] ?? "x"}__${row["Facility ID"] ?? "x"}`;
      if (!groups[key])
        groups[key] = {
          plantId: row["Plant ID"] ?? Infinity,
          facilityId: row["Facility ID"] ?? Infinity,
          label,
          rows: [],
        };
      groups[key].rows.push(row);
    });
    // Plant ascending; within a plant, facility descending so "No Facility"
    // (facilityId = Infinity) sorts before the assigned facilities.
    return Object.values(groups)
      .sort((a, b) => a.plantId - b.plantId || b.facilityId - a.facilityId)
      .map(({ label, rows }) => [label, rows] as [string, InvoiceReviewRow[]]);
  }, [filteredRows]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "B&B ID",
        headerName: "B&B ID",
        width: 150,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
      { field: "Invoice #", headerName: "Invoice #", width: 140 },
      { field: "Payee", headerName: "Payee", width: 300 },
      {
        field: "Amount",
        headerName: "Amount",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Gross",
        headerName: "Gross",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Hours",
        headerName: "Hours",
        width: 90,
        valueFormatter: decimalFormatter,
      },
      {
        field: "Bill Rate",
        headerName: "Bill Rate",
        width: 120,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Rate Type",
        headerName: "Rate Type",
        width: 110,
        valueFormatter: (params) =>
          params.value
            ? String(params.value).charAt(0).toUpperCase() +
              String(params.value).slice(1)
            : "",
      },
      {
        field: "Overtime Rate",
        headerName: "OT Rate",
        width: 120,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Other Additions",
        headerName: "Other Add.",
        width: 120,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Holiday",
        headerName: "Holiday",
        width: 100,
        valueFormatter: currencyFormatter,
      },
      {
        field: "PTO",
        headerName: "PTO",
        width: 90,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Status",
        headerName: "Status",
        width: 100,
        pinned: "right",
        cellRenderer: StatusCellRenderer,
      },
      { field: "Pay Period", headerName: "Pay Period", width: 170 },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 80,
    }),
    []
  );

  return (
    <div className="tw-p-6 tw-w-full tw-mx-auto">
      <div className="tw-w-full tw-flex tw-flex-col tw-justify-center">
        {error && (
          <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
            <div>{error}</div>
          </div>
        )}

        <ARInvoiceReviewFilters
          searchVal={searchVal}
          onSearchChange={setSearchVal}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          periodStart={periodStart}
          onPeriodStartChange={setPeriodStart}
        />

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="ar invoice review tabs"
          sx={{
            "& .MuiTabs-indicator": { display: "none" },
            minHeight: "20px",
          }}
        >
          <Tab
            label="Invoice Details"
            sx={{
              backgroundColor:
                activeTab === 0 ? "#2642c9 !important" : "white !important",
              color: activeTab === 0 ? "#fff !important" : "#000 !important",
              borderRadius: "6px 0px 0px 0px",
              minHeight: "30px !important",
              fontSize: "0.875rem",
              padding: "4px 12px",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  activeTab === 0 ? "#2642c9 !important" : "#d0d0d0 !important",
              },
            }}
          />
          <Tab
            label="Miscellaneous"
            sx={{
              backgroundColor:
                activeTab === 1 ? "#2642c9 !important" : "white !important",
              color: activeTab === 1 ? "#fff !important" : "#000 !important",
              minHeight: "30px !important",
              fontSize: "0.875rem",
              padding: "4px 12px",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  activeTab === 1 ? "#2642c9 !important" : "#d0d0d0 !important",
              },
            }}
          />
          <Tab
            label="Invoices"
            sx={{
              backgroundColor:
                activeTab === 2 ? "#2642c9 !important" : "white !important",
              color: activeTab === 2 ? "#fff !important" : "#000 !important",
              minHeight: "30px !important",
              fontSize: "0.875rem",
              padding: "4px 12px",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  activeTab === 2 ? "#2642c9 !important" : "#d0d0d0 !important",
              },
            }}
          />
          <Tab
            label="Invoice Differences"
            sx={{
              backgroundColor:
                activeTab === 3 ? "#2642c9 !important" : "white !important",
              color: activeTab === 3 ? "#fff !important" : "#000 !important",
              borderRadius: "0px 6px 0px 0px",
              minHeight: "30px !important",
              fontSize: "0.875rem",
              padding: "4px 12px",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  activeTab === 3 ? "#2642c9 !important" : "#d0d0d0 !important",
              },
            }}
          />
        </Tabs>

        {/* Tab 0: Invoice Details */}
        <div role="tabpanel" hidden={activeTab !== 0}>
          {isLoading && (
            <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
              <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
              Loading data...
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-text-gray-400">
              <SearchOffIcon sx={{ fontSize: 64 }} />
              <span className="tw-mt-2 tw-text-sm">
                No invoice data found for this period
              </span>
            </div>
          )}

          {!isLoading && rows.length > 0 && (
            <div className="tw-w-full">
              <div className="tw-rounded-r-lg tw-border tw-border-gray-200">
                {plantGroups.map(([plantName, plantRows]) => (
                  <div className="tw-bg-white tw-p-4 tw-mb-3" key={plantName}>
                    <PlantInvoiceGrid
                      plantName={plantName}
                      rows={plantRows}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      periodStart={periodStart}
                      onRefresh={refreshDetailsAndInvoices}
                      onFinalized={markRowsFinalized}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tab 1: Miscellaneous */}
        <div role="tabpanel" hidden={activeTab !== 1}>
          <MiscellaneousTab
            periodStart={periodStart}
            refreshSignal={miscRefreshKey}
            onAfterChange={() => setInvoiceRefreshKey((k) => k + 1)}
          />
        </div>

        {/* Tab 2: Invoices */}
        <div role="tabpanel" hidden={activeTab !== 2}>
          <InvoicesTab
            periodStart={periodStart}
            refreshSignal={invoiceRefreshKey}
            onAfterGenerate={() => {
              fetchReviewData(periodStart);
              setMiscRefreshKey((k) => k + 1);
            }}
          />
        </div>

        {/* Tab 3: Invoice Differences (read-only) */}
        <div role="tabpanel" hidden={activeTab !== 3}>
          <InvoiceDifferencesTab
            periodStart={periodStart}
            refreshSignal={invoiceRefreshKey}
          />
        </div>
      </div>
    </div>
  );
}

export default ARInvoiceReviewPage;
